import { useState, useEffect, useMemo } from 'react'
import { toast } from 'sonner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUserPlus } from '@fortawesome/free-solid-svg-icons'
import { Button } from '../shared/components/ui'
import GuestInvitationDialog from './GuestInvitationDialog'
import GuestUserList from './GuestUserList'
import DeleteDialog from './admin/DeleteDialog'
import { guestApi, type PendingInvitation } from '../services/guestApi'
import { useAccountContext } from '../contexts/AccountContext'
import type { GuestAccess, GuestAccessLevel, GroupedGuestUser } from '../types/guest'

interface Device {
  id: number
  name: string
  wisolId?: string
  location?: string
}

/**
 * Convert pending invitations to GroupedGuestUser format
 */
function convertPendingInvitationsToGroupedGuests(
  pendingInvitations: PendingInvitation[],
  allDevices: Device[]
): GroupedGuestUser[] {
  return pendingInvitations.map((invitation) => {
    // Create device list showing which devices the pending user has been invited to
    const pendingDeviceIds = new Set(invitation.pending_devices.map((d) => d.device_id))

    const devices = allDevices.map((device) => ({
      accessId: '',
      deviceId: device.id,
      deviceName: device.name,
      location: device.location,
      wisolId: device.wisolId,
      hasAccess: pendingDeviceIds.has(device.id),
    }))

    return {
      email: invitation.email,
      guestUserId: invitation.id,
      status: 'pending' as const,
      invitedDate: invitation.created_at,
      accessibleDevices: invitation.pending_devices.length,
      devices,
    }
  })
}

/**
 * Group flat guest list by user email (for device-sharing API)
 */
function groupGuestsByUser(guests: GuestAccess[], allDevices: Device[]): GroupedGuestUser[] {
  const grouped = new Map<string, GroupedGuestUser>()

  // First, group existing access grants by email
  for (const guest of guests) {
    if (!grouped.has(guest.guestEmail)) {
      grouped.set(guest.guestEmail, {
        email: guest.guestEmail,
        guestUserId: guest.guestUserId,
        status: guest.status,
        invitedDate: guest.createdAt,
        accessibleDevices: 0,
        devices: [],
      })
    }

    const user = grouped.get(guest.guestEmail)!

    // Only count active access grants
    if (guest.status !== 'revoked') {
      user.accessibleDevices++
    }

    user.devices.push({
      accessId: guest.id,
      deviceId: guest.deviceId,
      deviceName: guest.deviceName,
      hasAccess: guest.status !== 'revoked',
      accessLevel: guest.accessLevel,
    })

    // Update status to most recent (if registered, keep active)
    if (guest.status === 'active') {
      user.status = 'active'
      user.registeredDate = guest.createdAt
    }
  }

  // Then, for each user, add devices they don't have access to
  for (const user of grouped.values()) {
    const existingDeviceIds = new Set(user.devices.map((d) => d.deviceId))

    for (const device of allDevices) {
      if (!existingDeviceIds.has(device.id)) {
        user.devices.push({
          accessId: '',
          deviceId: device.id,
          deviceName: device.name,
          location: device.location,
          wisolId: device.wisolId,
          hasAccess: false,
        })
      }
    }

    // Sort devices: accessible first, then by name
    user.devices.sort((a, b) => {
      if (a.hasAccess !== b.hasAccess) {
        return a.hasAccess ? -1 : 1
      }
      return a.deviceName.localeCompare(b.deviceName)
    })
  }

  // Convert to array and sort by email
  return Array.from(grouped.values()).sort((a, b) => a.email.localeCompare(b.email))
}

export default function GuestAccessTab() {
  const { devices: accountDevices } = useAccountContext()
  const [guests, setGuests] = useState<GuestAccess[]>([])
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false)
  const [isRevokeDialogOpen, setIsRevokeDialogOpen] = useState(false)
  const [selectedUserForRevoke, setSelectedUserForRevoke] = useState<{
    email: string
    deviceCount: number
    isPending?: boolean
  } | null>(null)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedUserForDelete, setSelectedUserForDelete] = useState<{
    email: string
  } | null>(null)

  // Transform account devices to the format needed
  const devices: Device[] = useMemo(() => {
    return Object.values(accountDevices).map((device) => ({
      id: device.deviceId,
      name: device.deviceName,
      wisolId: device.wisolId,
      location: device.siteName || device.description,
    }))
  }, [accountDevices])

  // Combine active guests and pending invitations
  const groupedUsers = useMemo(() => {
    // Get active/registered guests from device_access
    const activeGuests = groupGuestsByUser(guests, devices)

    // Get pending invitations (users who haven't registered)
    const pendingGuests = convertPendingInvitationsToGroupedGuests(pendingInvitations, devices)

    // Merge: pending invitations first, then active guests
    // Filter out any duplicates (same email)
    const activeEmails = new Set(activeGuests.map((g) => g.email))
    const filteredPending = pendingGuests.filter((p) => !activeEmails.has(p.email))

    return [...filteredPending, ...activeGuests]
  }, [guests, pendingInvitations, devices])

  // Load guests and pending invitations
  const loadGuests = async () => {
    setIsLoading(true)
    setError(null)

    try {
      // Fetch both active guests and pending invitations in parallel
      const [guestsResponse, pendingResponse] = await Promise.all([
        guestApi.listGuests(1, 100, {}),
        guestApi.getPendingInvitations(1, 100),
      ])

      setGuests(guestsResponse.guests)
      setPendingInvitations(pendingResponse.invitations)
      setHasLoadedOnce(true)
    } catch (err: any) {
      console.error('Failed to load guests:', err)
      setError(err.message || 'Failed to load guest list')
      setHasLoadedOnce(true) // Mark as loaded even on error so we show error state, not spinner forever
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadGuests()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleInviteGuest = async (email: string, deviceIds: number[], accessLevel: GuestAccessLevel) => {
    try {
      await guestApi.inviteGuest({ email, deviceIds, accessLevel })

      toast.success(`Guest invitation sent to ${email} for ${deviceIds.length} device(s)`, { duration: 15000 })

      await loadGuests()
      setIsInviteDialogOpen(false)
    } catch (err: any) {
      console.error('Failed to invite guest:', err)
      toast.error(`Failed to invite guest: ${err.message || 'Unknown error'}`, { duration: 30000 })
      throw err
    }
  }

  const handleToggleDevice = async (guestEmail: string, deviceId: number, grant: boolean) => {
    try {
      if (grant) {
        // Grant access - invite guest to device
        await guestApi.inviteGuest({
          email: guestEmail,
          deviceIds: [deviceId],
          accessLevel: 'VIEW_ONLY',
        })
      } else {
        // Revoke access - find the access ID and revoke
        const user = groupedUsers.find((u) => u.email === guestEmail)
        const device = user?.devices.find((d) => d.deviceId === deviceId)

        if (device?.accessId) {
          await guestApi.revokeGuestAccess(device.accessId)
        }
      }

      await loadGuests()
    } catch (err: any) {
      console.error('Failed to toggle device access:', err)
      toast.error(`Failed to update device access: ${err.message || 'Unknown error'}`, { duration: 30000 })
      throw err
    }
  }

  const handleRevokeAll = (guestEmail: string) => {
    const user = groupedUsers.find((u) => u.email === guestEmail)
    if (user) {
      setSelectedUserForRevoke({
        email: user.email,
        deviceCount: user.accessibleDevices,
        isPending: user.status === 'pending',
      })
      setIsRevokeDialogOpen(true)
    }
  }

  const confirmRevokeAll = async () => {
    if (!selectedUserForRevoke) return

    try {
      const user = groupedUsers.find((u) => u.email === selectedUserForRevoke.email)
      if (!user) return

      // Check if this is a pending user (not yet registered)
      const isPendingUser = user.status === 'pending'

      if (isPendingUser) {
        // For pending users, delete the pending invitation entirely
        // This removes: pending_accounts, pending_device_access, and Supabase user
        await guestApi.deletePendingInvitation(user.guestUserId)
        toast.success(`Invitation deleted for ${selectedUserForRevoke.email}`, { duration: 15000 })
      } else {
        // For registered users, use bulk revoke endpoint (single API call)
        // This is much more efficient than revoking each device individually
        const result = await guestApi.revokeAllGuestAccess(selectedUserForRevoke.email)
        toast.success(`Revoked access to ${result.revoked_count} device(s) for ${selectedUserForRevoke.email}`, {
          duration: 15000,
        })
      }

      await loadGuests()
      setIsRevokeDialogOpen(false)
      setSelectedUserForRevoke(null)
    } catch (err: any) {
      console.error('Failed to revoke/delete:', err)
      toast.error(`Failed to revoke access: ${err.message || 'Unknown error'}`, { duration: 30000 })
    }
  }

  const handleResendInvite = async (guestEmail: string) => {
    try {
      const user = groupedUsers.find((u) => u.email === guestEmail)
      if (!user) return

      // Only pending users can have invitations resent
      if (user.status !== 'pending') {
        toast.error('Can only resend invitations to pending users', { duration: 30000 })
        return
      }

      // Use the dedicated resend endpoint with the pending account ID
      await guestApi.resendInvitation(user.guestUserId)

      toast.success(`Invitation resent to ${guestEmail}`, { duration: 15000 })
    } catch (err: any) {
      console.error('Failed to resend invitation:', err)
      toast.error(`Failed to resend invitation: ${err.message || 'Unknown error'}`, { duration: 30000 })
    }
  }

  const handleDeleteRevoked = (guestEmail: string) => {
    setSelectedUserForDelete({ email: guestEmail })
    setIsDeleteDialogOpen(true)
  }

  const confirmDeleteRevoked = async () => {
    if (!selectedUserForDelete) return

    try {
      await guestApi.deleteGuestAccess(selectedUserForDelete.email)
      toast.success(`All access records deleted for ${selectedUserForDelete.email}`, { duration: 15000 })

      await loadGuests()
      setIsDeleteDialogOpen(false)
      setSelectedUserForDelete(null)
    } catch (err: any) {
      console.error('Failed to delete guest access:', err)
      toast.error(`Failed to delete guest: ${err.message || 'Unknown error'}`, { duration: 30000 })
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Guest Access Management</h2>
          <p className="text-gray-600 mt-1">
            Invite guests to access your devices and manage their permissions
            {isLoading && hasLoadedOnce && <span className="ml-2 text-sm text-blue-600">Refreshing...</span>}
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => setIsInviteDialogOpen(true)}
          className="flex items-center"
          disabled={isLoading}
        >
          <FontAwesomeIcon icon={faUserPlus} className="mr-2 h-4 w-4" />
          Invite Guest
        </Button>
      </div>

      {/* Guest User List - show spinner on initial load, keep list visible during refresh */}
      <GuestUserList
        users={groupedUsers}
        isLoading={isLoading}
        hasLoadedOnce={hasLoadedOnce}
        error={error}
        onToggleDevice={handleToggleDevice}
        onRevokeAll={handleRevokeAll}
        onResendInvite={handleResendInvite}
        onDeleteRevoked={handleDeleteRevoked}
      />

      {/* Invitation Dialog */}
      <GuestInvitationDialog
        open={isInviteDialogOpen}
        onClose={() => setIsInviteDialogOpen(false)}
        onInvite={handleInviteGuest}
        devices={devices}
        existingGuestEmails={groupedUsers.map((u) => u.email)}
      />

      {/* Revoke/Delete Confirmation Dialog */}
      <DeleteDialog
        open={isRevokeDialogOpen}
        onClose={() => {
          setIsRevokeDialogOpen(false)
          setSelectedUserForRevoke(null)
        }}
        onConfirm={confirmRevokeAll}
        title={selectedUserForRevoke?.isPending ? 'Delete Invitation' : 'Revoke All Access'}
        message={
          selectedUserForRevoke?.isPending
            ? `Are you sure you want to delete the invitation for ${selectedUserForRevoke?.email}? This will remove them from pending accounts and delete their Supabase auth user.`
            : `Are you sure you want to revoke all access for ${selectedUserForRevoke?.email}? They will lose access to ${selectedUserForRevoke?.deviceCount} device(s).`
        }
        isLoading={false}
      />

      {/* Delete Revoked User Confirmation Dialog */}
      <DeleteDialog
        open={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false)
          setSelectedUserForDelete(null)
        }}
        onConfirm={confirmDeleteRevoked}
        title="Delete Revoked User"
        message={`Are you sure you want to completely remove ${selectedUserForDelete?.email} from your guest list? This will delete all access records for this user.`}
        isLoading={false}
      />
    </div>
  )
}
